

// Event.path is non-standard and used in some older browsers
type NonStandardEvent = Omit<Event, 'composedPath'> & {
    path: EventTarget[];
};

/**
 * This method returns the paths for the target event 
 * @param {Event | NonStandardEvent} event 
 * @returns {EventTarget[] | null}
 */
function getEventPath(event: Event | NonStandardEvent): EventTarget[] | null {
    try {
        if ('composedPath' in event) {
            const path = event.composedPath();
            if (path.length) {
                return path;
            }
        } else if ('path' in event && event.path.length) {
            return event.path;
        }
        return null;
    } catch {
        return null;
    }
}

/**
 * This method generates a path for node clicked
 * @param {path: EventTarget[] | null} path 
 * @returns {string}
 */
function generateNodePath(path: EventTarget[] | null): string {
    if (!path || !Array.isArray(path)) {
        return '';
    }
    let nodePath = '';
    for (let i = path.length > 3 ? 3 : path.length - 1; i >= 0; i--) {
        let intermediateNodePath = '';
        let aPath: any = path[i];
        if (aPath && aPath.tagName && aPath.attributes && aPath.attributes.length) {
            if (i > 0)
                intermediateNodePath = aPath.tagName.toLowerCase() + getNodeAttributeValues(aPath.attributes) + ' > ';
            else
                intermediateNodePath = aPath.tagName.toLowerCase() + getNodeAttributeValues(aPath.attributes);
            nodePath = nodePath + intermediateNodePath;
        }
    }
    return nodePath;
}

/**
 * This method generate attribute path in string
 * @param attrObj 
 * @returns 
 */
function getNodeAttributeValues(attrObj: any) {
    let attributeValue = '';
    if (!attrObj || (attrObj && !attrObj.length)) {
        return attributeValue;
    }

    Object.values(attrObj).forEach((attr: any) => {
        if (attr && attr.nodeName) {
            let key = attr.nodeName;
            let value = attr.nodeValue;
            if (key == 'class') {
                attributeValue = attributeValue + '.' + value;
            } else if (key == 'id') {
                attributeValue = attributeValue + '#' + value;
            } else if (key == 'type') {
                attributeValue = attributeValue + `[${key}="${value}"]`;
            }
        }
    });

    return attributeValue;
}

/**
 * This method will return the path from Event and EventTarget
 * @param {Event} event 
 * @returns {string}
 */
export function getNodePathFromEvent(event: Event) {
    const eventPath: EventTarget[] | null = getEventPath(event);
    return eventPath ? generateNodePath(eventPath) : '';
}

/**
 * This method will check if the click event is dead click
 * @param event 
 * @returns 
 */
export function isDeadClick(event: MouseEvent | TouchEvent): boolean {
    const target: any = event.target;
    const nodeName: string = target ? target?.nodeName.toLowerCase() : '';
  
    const isClickable =
      ((target instanceof HTMLAnchorElement) || (target.onclick !== null) || (target.hasAttribute('onclick')));
  
    if (nodeName === 'a' || isClickable) {
      return false;
    } else {
      return true;
    }
}  

/**
 * This method will extract the inner text of the button element
 * @param event 
 * @returns 
 */
export function getTextContent(event: MouseEvent | TouchEvent): string {
    let eventTarget: any = event.target;
    // Check if event.target is defined and not null
    if (eventTarget) {
      const innerText: string = eventTarget.innerText || '';
      return innerText;
    } else {
      console.error("Event target is not an HTML element.");
      return '';
    }
}

/**
 * This method will find the height and width of the clicked element
 * @param event 
 * @returns 
 */
export function getElementSize(event: MouseEvent | TouchEvent): number[] {
    let eventTarget: any = event.target;
    // Check if event.target is defined and not null
    if (eventTarget) {
      const elementHeight : number = eventTarget.clientHeight;
      const elementWidth : number = eventTarget.clientWidth;
      return [elementHeight, elementWidth];
    } else {
      console.error("Event target is not an HTML element.");
      return [0,0];
    }
}

/**
 * This method will calculate the unique selector from the event data
 * @param event 
 * @returns 
 */
export function getUniqueSelector(event: MouseEvent | TouchEvent): string | null {
    const numberOfAncestorOrigins = (event?.target as HTMLElement)?.ownerDocument?.location?.ancestorOrigins?.length || 0;
    
    let eventTarget: any = event?.target;
    if (eventTarget) {
        if (!eventTarget || !eventTarget?.parentNode) {
            return null;
        }
        const selector: string[] = [];
        
        let target: any = event?.target;
        while (target && target?.tagName) {
            let tagName = target.tagName.toLowerCase();
            let index = Array.from(target.parentNode!.children).indexOf(target) + 1;

            if (tagName !== "body") {
                const indexStr = index > 1 ? `:nth-child(${index})` : "";
                selector.unshift(tagName + indexStr);
            } else {
                selector.unshift(tagName);
            }

            target = target.parentNode ? target.parentNode : null;
        }

        if(numberOfAncestorOrigins > 0){ 
            const elementInIframeIdentifierText = `iframe_context.level-${numberOfAncestorOrigins}`;
            selector.unshift(elementInIframeIdentifierText); /* adding iframe_context.level-<numberOfAncestorOrigins> for elements inside iframe, 
            using this in frontend we will come to know about the element is in iframe, so we will perform search for this 
            element in iframe */
        }

        return selector.join(" > ");
    }
    
    return null;
}